import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Search } from 'lucide-react';
import { showGlobalNotification } from '../hooks/useNotifications';

const API_BASE_URL = import.meta.env.VITE_API_URL;

interface Brand {
    id: number;
    name: string;
    logo_url?: string;
    description?: string;
    is_active: boolean;
}

const ComputerBrandsPage: React.FC = () => {
    const [brands, setBrands] = useState<Brand[]>([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingBrand, setEditingBrand] = useState<Brand | null>(null);
    const [searchTerm, setSearchTerm] = useState('');

    const [brandData, setBrandData] = useState({
        name: '',
        description: '',
        logo: null as File | null,
        is_active: true
    });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('auth_token');
            const response = await fetch(`${API_BASE_URL}/computers/computer-brands`, { headers: { 'Authorization': `Bearer ${token}` } });
            const data = await response.json();
            setBrands(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error loading brands:', error);
            showGlobalNotification.error('Hata', 'Markalar yüklenirken hata oluştu');
            setBrands([]);
        } finally {
            setLoading(false);
        }
    };

    const resetBrandForm = () => {
        setBrandData({
            name: '',
            description: '',
            logo: null,
            is_active: true
        });
        setEditingBrand(null);
    };

    const handleSaveBrand = async () => {
        if (!brandData.name.trim()) {
            showGlobalNotification.error('Hata', 'Marka adı zorunludur');
            return;
        }

        try {
            const token = localStorage.getItem('auth_token');
            const formData = new FormData();
            formData.append('name', brandData.name);
            formData.append('description', brandData.description);
            formData.append('is_active', brandData.is_active.toString());
            if (brandData.logo) {
                formData.append('logo', brandData.logo);
            }

            const url = editingBrand
                ? `${API_BASE_URL}/computers/computer-brands/${editingBrand.id}`
                : `${API_BASE_URL}/computers/computer-brands`;

            const response = await fetch(url, {
                method: editingBrand ? 'PUT' : 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Kayıt başarısız');
            }

            showGlobalNotification.success('Başarılı', editingBrand ? 'Marka güncellendi' : 'Marka eklendi');
            setShowModal(false);
            resetBrandForm();
            loadData();
        } catch (error: any) {
            showGlobalNotification.error('Hata', error.message);
        }
    };

    const handleEditBrand = (brand: Brand) => {
        setEditingBrand(brand);
        setBrandData({
            name: brand.name,
            description: brand.description || '',
            logo: null,
            is_active: brand.is_active
        });
        setShowModal(true);
    };

    const handleDeleteBrand = async (id: number) => {
        if (!confirm('Bu markayı silmek istediğinizden emin misiniz?')) return;

        try {
            const token = localStorage.getItem('auth_token');
            const response = await fetch(`${API_BASE_URL}/computers/computer-brands/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Silme başarısız');
            }

            showGlobalNotification.success('Başarılı', 'Marka silindi');
            loadData();
        } catch (error: any) {
            showGlobalNotification.error('Hata', error.message);
        }
    };
    
    const filteredBrands = brands.filter(brand =>
        brand.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) {
        return <div className="flex justify-center items-center h-screen"><div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-primary"></div></div>;
    }

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold">Bilgisayar Markaları</h1>
                <button
                    onClick={() => { resetBrandForm(); setShowModal(true); }}
                    className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark flex items-center"
                >
                    <Plus className="w-5 h-5 mr-2" />
                    Yeni Marka Ekle
                </button>
            </div>

            <div className="mb-4">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                        type="text"
                        placeholder="Marka ara..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border rounded-lg"
                    />
                </div>
            </div>

            <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Logo</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Marka</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Açıklama</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Durum</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">İşlemler</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {filteredBrands.map(brand => (
                            <tr key={brand.id}>
                                <td className="px-6 py-4">
                                    {brand.logo_url && (
                                        <img src={`${API_BASE_URL.replace('/api', '')}${brand.logo_url}`} alt={brand.name} className="w-16 h-16 object-contain" />
                                    )}
                                </td>
                                <td className="px-6 py-4">{brand.name}</td>
                                <td className="px-6 py-4">{brand.description}</td>
                                <td className="px-6 py-4">
                                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${brand.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                        {brand.is_active ? 'Aktif' : 'Pasif'}
                                    </span>
                                </td>
                                <td className="px-6 py-4">
                                    <button onClick={() => handleEditBrand(brand)} className="text-primary hover:text-primary-dark mr-4"><Edit className="w-5 h-5" /></button>
                                    <button onClick={() => handleDeleteBrand(brand.id)} className="text-red-600 hover:text-red-900"><Trash2 className="w-5 h-5" /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {showModal && (
                 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                 <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full">
                     <div className="p-6">
                         <div className="flex justify-between items-center mb-4">
                             <h2 className="text-xl font-bold">{editingBrand ? 'Markayı Düzenle' : 'Yeni Marka Ekle'}</h2>
                             <button onClick={() => { setShowModal(false); resetBrandForm(); }} className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300">&times;</button>
                         </div>
                         <div className="space-y-4">
                             <div>
                                 <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Marka Adı *</label>
                                 <input
                                     type="text"
                                     value={brandData.name}
                                     onChange={(e) => setBrandData({ ...brandData, name: e.target.value })}
                                     className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                                 />
                             </div>
                             <div>
                                 <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Açıklama</label>
                                 <textarea
                                     value={brandData.description}
                                     onChange={(e) => setBrandData({ ...brandData, description: e.target.value })}
                                     rows={3}
                                     className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                                 />
                             </div>
                             <div>
                                 <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Logo</label>
                                 <input
                                     type="file"
                                     accept="image/*"
                                     onChange={(e) => setBrandData({ ...brandData, logo: e.target.files?.[0] || null })}
                                     className="mt-1 block w-full text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary/10 file:text-primary-dark hover:file:bg-primary/20"
                                 />
                             </div>
                             <div className="flex items-center">
                                 <input
                                     type="checkbox"
                                     id="brand_is_active"
                                     checked={brandData.is_active}
                                     onChange={(e) => setBrandData({ ...brandData, is_active: e.target.checked })}
                                     className="h-4 w-4 text-primary focus:ring-primary border-gray-300 dark:border-gray-600 rounded"
                                 />
                                 <label htmlFor="brand_is_active" className="ml-2 block text-sm text-gray-900 dark:text-gray-100">Aktif</label>
                             </div>
                         </div>
                         <div className="mt-6 flex justify-end space-x-3">
                             <button
                                 onClick={() => { setShowModal(false); resetBrandForm(); }}
                                 className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-300"
                             >
                                 İptal
                             </button>
                             <button
                                 onClick={handleSaveBrand}
                                 className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
                             >
                                 {editingBrand ? 'Güncelle' : 'Kaydet'}
                             </button>
                         </div>
                     </div>
                 </div>
             </div>
            )}
        </div>
    );
};

export default ComputerBrandsPage;
